import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { motion, AnimatePresence } from "framer-motion";
import { IconX, IconArrowRight, IconSparkles, IconMinus } from "@tabler/icons-react";
import { getOnboarding, setTourState } from "@/lib/api/handlers/OnboardingHandling";
import { useAuth } from "@/lib/auth/AuthContext";
import { VideoPlayerRiv } from "@/components/VideoPlayer/VideoPlayerRiv";
import { useCoachStore, COACH_RESTART_FLAG_KEY as RESTART_FLAG_KEY } from "@/lib/onboarding/coach-store";
import { resolveCoachStop, type CoachContext } from "@/components/POnboarding/coachSteps";
import { getActiveSteps, ONBOARDING_TRACKS } from "@/components/POnboarding/onboarding-steps";
import { waitForAnchor } from "@/components/POnboarding/tour/waitForAnchor";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { isAnyBuilderRoute, isCreateEventRoute } from "@/lib/immersiveRoutes";
import { normalizePathname } from "@/lib/routing/activePath";
import "./onboarding-coach.css";

type Progress = {
  companyRole: string;
  eventsUsed: number;
  welcomeShown?: boolean;
  tourState?: "PENDING" | "COMPLETED" | "SKIPPED";
  completedSteps: Record<string, string>;
  // Captured so the restart greeting can name the user's real next step.
  activeTracks: string[];
  customerType: string | null;
  hasSubscription: boolean;
  hasCompanyProfile: boolean;
};

// Onboarding is company-wide; plugins are per-event, so (like OnboardingProvider)
// we pass every known plugin to keep the step list stable across events.
const ALL_PLUGINS = [
  "DASHBOARD", "WEBSITE", "MESSAGING", "SLOTTING",
  "CHECK_IN", "SEATING", "ACTIONS", "ATTACHMENTS", "DOCUMENTS", "GOALS",
  "ATTENDEE_APP", "PROGRAM", "SPEAKERS", "SPONSORS",
];

/** The programme page hosts the timeline grid in the bottom-right corner the
 *  coach docks to — the coach starts COLLAPSED there (pill only). Exported
 *  for tests. Matches /event/:id/programme and its sub-routes. */
export const isProgrammeRoute = (pathname: string): boolean =>
  /^\/event\/[^/]+\/programme(\/|$)/.test(pathname);

/**
 * Persistent hands-on onboarding "coach": a small docked companion (Rive
 * character + concise tip) that travels the whole first-run journey —
 * dashboard → every create-event wizard step → the real event's surfaces —
 * showing a tailored tip per step and pulse-ringing the next action. App-level
 * and self-contained (fetches its own onboarding progress) so it survives the
 * dashboard ↔ shell-less /create-event ↔ event-shell navigation. Inert for
 * everyone but a brand-new OWNER mid-journey (welcomeShown && tourState PENDING).
 */
export function OnboardingCoach() {
  // Canonical (no trailing slash): every rule below compares against
  // slash-less route literals — see lib/routing/activePath.
  const pathname = normalizePathname(useLocation().pathname);
  const navigate = useNavigate();
  // Match the create-event wizard's OWN layout breakpoint (lg = 1024px): above it
  // the StepNavigation panel sits on the right; below it the MobileStepBar is
  // fixed to the bottom. The coach docks to clear whichever is in play.
  const isWideWizard = useMediaQuery("(min-width: 1024px)");
  const t = useTranslations("onboarding.coach");
  // Second translator for step titles (the "onboarding" root namespace) so the
  // restart greeting can name the user's real next step.
  const tOnb = useTranslations("onboarding");

  const wizardMode = useCoachStore((s) => s.wizardMode);
  const wizardStep = useCoachStore((s) => s.wizardStep);
  const setJourneyActive = useCoachStore((s) => s.setJourneyActive);
  // True while any blocking full-screen / docked overlay is open (offering
  // wizard, lane popover, appointment modal, …). The coach hides so its fixed
  // bottom-right card never covers the overlay's action buttons.
  const overlayOpen = useCoachStore((s) => s.overlayCount > 0);
  // CRM transfer mode docks its contact pane in this card's bottom-right slot —
  // hide like the checklist panel does (narrow signal — see coach-store).
  const crmSuppressed = useCoachStore((s) => s.crmModeCount > 0);

  const [progress, setProgress] = useState<Progress | null>(null);
  const [ended, setEnded] = useState(false);
  // Explicit user-driven restart (user-settings → "Restart intro"). Drives the
  // restart-specific dashboard stop for returning users with events.
  const [restarted, setRestarted] = useState(() => {
    try {
      return localStorage.getItem(RESTART_FLAG_KEY) === "1";
    } catch {
      return false;
    }
  });
  // Optimistically-completed steps from `onboarding-step-complete` events (e.g.
  // adding the first guest) so the coach reacts INSTANTLY, before the backend
  // round-trip — the next progress fetch then confirms them.
  const [optimisticSteps, setOptimisticSteps] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  useEffect(() => {
    const onStep = (e: Event) => {
      const step = (e as CustomEvent<{ step?: string }>).detail?.step;
      if (typeof step === "string") {
        setOptimisticSteps((prev) => new Set(prev).add(step));
      }
    };
    window.addEventListener("onboarding-step-complete", onStep as EventListener);
    return () =>
      window.removeEventListener("onboarding-step-complete", onStep as EventListener);
  }, []);
  // Gates the FIRST paint until the one-shot auto-graduate check has run, so a
  // returning already-done user is suppressed before render (no one-frame flash)
  // — without folding `alreadyDone` into journeyActive, which would wrongly hide
  // the coach the instant a brand-new user completes a step LIVE mid-journey.
  const [graduationChecked, setGraduationChecked] = useState(false);
  const graduatedRef = useRef(false);

  // Self-fetch onboarding progress (the per-shell OnboardingProvider isn't in
  // scope on /create-event).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  // Auth readiness gate. The coach is mounted at App root — OUTSIDE PrivateRoute
  // — so on a cold load it would fire getOnboarding() in parallel with
  // AuthContext's bootstrap token-refresh, hitting the backend with the expired
  // access cookie and logging a 401 (the axios interceptor then refreshes and
  // retries, so it "works" but pollutes the console every session). Waiting for
  // `authenticated` means the bootstrap refresh has already run, so the first
  // call carries a fresh token. Read via a ref so `load`'s identity stays stable.
  const { state: authState } = useAuth();
  const authed = authState.kind === "authenticated";
  const authedRef = useRef(authed);
  useEffect(() => {
    authedRef.current = authed;
  }, [authed]);
  const load = useCallback(() => {
    // Logged-out / auth pages have no companyId — skip the doomed call.
    if (window.location.pathname.startsWith("/auth")) return;
    // Not yet authenticated (bootstrap refresh still in flight) — skip so we
    // don't race it into a 401. Re-fires from the mount effect once authed.
    if (!authedRef.current) return;
    getOnboarding().then((res) => {
      if (aliveRef.current && res && !("isServerError" in res)) {
        setProgress({
          companyRole: res.companyRole,
          eventsUsed: res.eventsUsed ?? 0,
          welcomeShown: res.welcomeShown,
          tourState: res.tourState,
          completedSteps: res.completedSteps ?? {},
          activeTracks: res.activeTracks ?? [],
          customerType: res.customerType ?? null,
          hasSubscription: res.hasSubscription ?? false,
          hasCompanyProfile: res.hasCompanyProfile ?? false,
        });
      }
    });
  }, []);

  // Mount + explicit "onboarding-refetch" signal (same source the provider uses).
  // `authed` in the deps re-runs this once the bootstrap refresh resolves, so a
  // cold load fetches exactly once — with a fresh token — instead of racing a 401.
  useEffect(() => {
    load();
    const onRefetch = () => load();
    window.addEventListener("onboarding-refetch", onRefetch);
    return () => window.removeEventListener("onboarding-refetch", onRefetch);
  }, [load, authed]);

  // Explicit re-open (user-settings → "Restart intro"): the menu has already
  // PUT tourState=PENDING and set the restart flag, so clearing the local
  // `ended` latch + refetching revives the coach in-session (and the flag keeps
  // it alive across a subsequent reload, via the auto-graduate guard below).
  useEffect(() => {
    const onRestart = () => {
      setEnded(false);
      setRestarted(true);
      load();
    };
    window.addEventListener("onboarding-coach-restart", onRestart);
    return () => window.removeEventListener("onboarding-coach-restart", onRestart);
  }, [load]);

  const isOwner = progress?.companyRole?.toUpperCase() === "OWNER";
  const journeyActive =
    !ended &&
    isOwner &&
    progress?.welcomeShown === true &&
    progress?.tourState === "PENDING";

  // While the journey is active, refresh on navigation so step completions
  // (create_event after the wizard, add_first_guest) are reflected and the
  // auto-graduate check fires. Gated on journeyActive so the app-wide coach
  // never polls for the (vast majority of) non-journey users.
  useEffect(() => {
    if (journeyActive) load();
  }, [pathname, journeyActive, load]);

  // Auto-graduate — but ONLY from the FIRST progress snapshot: a returning user
  // who already finished the core actions (tourState still PENDING) shouldn't be
  // coached again. We must NOT graduate on a LIVE completion mid-session, or the
  // coach would vanish the instant `add_first_guest` lands — right when it should
  // keep guiding the user through website / mail / seating.
  useEffect(() => {
    if (!progress || graduatedRef.current) return;
    graduatedRef.current = true; // run this check exactly once
    // Honour an explicit user-driven restart: never auto-graduate it away, even
    // for a user who already finished the core steps.
    let restarted = false;
    try {
      restarted = localStorage.getItem(RESTART_FLAG_KEY) === "1";
    } catch {
      // localStorage unavailable
    }
    const done = progress.completedSteps;
    const alreadyDone = !!done["create_event"] && !!done["add_first_guest"];
    if (!restarted && isOwner && progress.welcomeShown === true && progress.tourState === "PENDING" && alreadyDone) {
      void setTourState("COMPLETED");
      setEnded(true);
    }
    setGraduationChecked(true); // first paint may proceed (batched with setEnded)
  }, [progress, isOwner]);

  const eventId = useMemo(() => {
    const m = pathname.match(/\/event\/([^/]+)/);
    return m?.[1] ?? null;
  }, [pathname]);

  const isAuthRoute = pathname.startsWith("/auth");
  // All builders, NOT /create-event: the coach deliberately guides the wizard.
  const isImmersiveRoute = isAnyBuilderRoute(pathname);
  const isE2eMode = import.meta.env.MODE === "e2e";

  const stop = useMemo(() => {
    if (!journeyActive) return null;
    const completedSteps = { ...(progress?.completedSteps ?? {}) };
    for (const s of optimisticSteps) completedSteps[s] ||= "1";
    const ctx: CoachContext = {
      pathname,
      eventId,
      eventsUsed: progress?.eventsUsed ?? 0,
      wizardMode,
      wizardStep,
      completedSteps,
      restarted,
    };
    return resolveCoachStop(ctx);
  }, [journeyActive, pathname, eventId, wizardMode, wizardStep, progress, optimisticSteps, restarted]);

  // The user's real next step (first incomplete), so the restart greeting can
  // say "here's what's still open: X" instead of a generic tour replay. Mirrors
  // OnboardingProvider's merge of the live hasSubscription / hasCompanyProfile
  // flags into completedSteps. null once everything is done.
  const nextStepTitle = useMemo(() => {
    if (!progress) return null;
    const merged = { ...progress.completedSteps };
    for (const s of optimisticSteps) merged[s] ||= "1";
    if (progress.hasSubscription) merged["choose_plan"] ||= "1";
    if (progress.hasCompanyProfile) merged["complete_company_profile"] ||= "1";
    const steps = getActiveSteps(
      ONBOARDING_TRACKS,
      progress.activeTracks,
      progress.companyRole,
      ALL_PLUGINS,
      merged,
      progress.customerType,
    );
    const next = steps.find((s) => !s.isCompleted);
    return next ? tOnb(next.titleKey) : null;
  }, [progress, optimisticSteps, tOnb]);

  // restartDash normally shows a generic "welcome back" line; when there IS a
  // remaining step, name it so the restart reflects the user's actual reality.
  const bodyText =
    stop?.id === "restartDash" && nextStepTitle
      ? t("restartDash.bodyNext", { step: nextStepTitle })
      : stop
        ? t(stop.bodyKey)
        : "";

  // Publish "the coach owns the bottom-right slot" so POnboarding's checklist
  // defers to it. Gated on an actual matching stop — a PENDING tour with no tip
  // for the current route must NOT hide the checklist (that made "Restart
  // intro" look like it deleted onboarding instead of restarting it).
  useEffect(() => {
    setJourneyActive(!!journeyActive && !!stop);
  }, [journeyActive, stop, setJourneyActive]);

  // Dock the coach to clear the wizard's chrome. Only the *scratch* wizard renders
  // the step chrome: above lg the right-side StepNavigation panel (dock bottom-
  // LEFT to clear it); below lg the fixed ~83px MobileStepBar (lift to bottom-32,
  // since the coach is z-300 over the bar's z-30 and must not cover the
  // Back/Continue/Create tap targets). The choose / template-picker screens have
  // no fixed chrome and centred cards, so dock bottom-LEFT there. Bottom-right
  // everywhere outside the wizard.
  const isWizard = isCreateEventRoute(pathname);
  const inScratchWizard = isWizard && wizardMode === "scratch";
  // Right-docked variants shift with the AI drawer rail (see PBackgroundJobs);
  // the wizard closes the drawer, so its right-4 variant needs no offset, but
  // carrying it is harmless (--ai-rail-push is 0 there) and future-proof.
  const railRight =
    "right-4 sm:right-[calc(1rem+var(--ai-rail-push))] transition-[right] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none";
  const posClass = !isWizard
    ? `bottom-4 ${railRight}`
    : inScratchWizard
      ? isWideWizard
        ? "bottom-4 left-4"
        : `bottom-32 ${railRight}`
      : "bottom-4 left-4";

  const shouldShow =
    !!stop &&
    journeyActive &&
    graduationChecked &&
    !overlayOpen &&
    !crmSuppressed &&
    !isAuthRoute &&
    !isImmersiveRoute &&
    !isE2eMode;

  // Collapse/minimize (UX wave): explicit user choice wins; without one the
  // coach defaults to COLLAPSED on the programme page (its card would cover
  // the timeline grid's bottom-right corner) and expanded everywhere else.
  const [userCollapsed, setUserCollapsed] = useState<boolean | null>(null);
  const collapsed = userCollapsed ?? isProgrammeRoute(pathname);

  // Pulse-ring the current stop's target — gated on `shouldShow` so a ring is
  // only ever drawn while the coach card is actually visible. waitForAnchor
  // handles lazy-mounted surfaces; the class is removed on stop/route change.
  // The ring follows the active wizard step as the user scrolls (the stop's
  // highlight selector changes with wizardStep).
  useEffect(() => {
    if (!shouldShow || collapsed || !stop?.highlight) return;
    let el: HTMLElement | null = null;
    let cancelled = false;
    waitForAnchor(stop.highlight, { timeoutMs: 8000 }).then((found) => {
      if (cancelled || !found) return;
      el = found;
      el.classList.add("onboarding-coach-pulse");
    });
    return () => {
      cancelled = true;
      el?.classList.remove("onboarding-coach-pulse");
    };
  }, [shouldShow, collapsed, stop?.id, stop?.highlight, pathname]);

  const endJourney = async (state: "COMPLETED" | "SKIPPED") => {
    setEnded(true); // hide immediately
    // A deliberate end clears any restart override, so the next session returns
    // to normal auto-graduate suppression.
    try {
      localStorage.removeItem(RESTART_FLAG_KEY);
    } catch {
      // localStorage unavailable
    }
    setRestarted(false);
    // Persist BEFORE the refetch so a follow-up load() reads the new state and
    // can't repopulate PENDING (mirrors the welcome modal's awaited onSkip).
    await setTourState(state);
    window.dispatchEvent(new Event("onboarding-refetch"));
  };

  return createPortal(
    <AnimatePresence>
      {shouldShow && stop && (
        <motion.div
          key="onboarding-coach"
          // z-40: BELOW every modal layer (ModalWrapper base=z-50, high=1000)
          // so open modals always stack above the coach — while staying over
          // the wizard's MobileStepBar (z-30). Was z-300, which covered
          // zIndex="base" modals.
          className={`fixed ${posClass} z-40`}
          initial={{ opacity: 0, y: 12, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.96 }}
          transition={{ type: "spring", damping: 24, stiffness: 320 }}
        >
          {collapsed ? (
            // Minimized pill — expands on click.
            <button
              type="button"
              data-testid="onboarding-coach-pill"
              aria-label={t("expand")}
              onClick={() => setUserCollapsed(false)}
              className="flex items-center gap-1.5 h-8 px-3 rounded-full surface-card border outline-secondary shadow-lg body-3-medium font-primary hover:surface-quaternary transition-colors cursor-pointer"
            >
              <IconSparkles size={14} className="font-blue shrink-0" />
              {t(stop.titleKey)}
            </button>
          ) : (
          <div
            data-testid="onboarding-coach-card"
            className="surface-card border outline-secondary rounded-xl shadow-lg w-[90vw] sm:w-80 overflow-hidden font-primary"
          >
            <div className="flex items-start gap-3 p-3.5">
              <div className="shrink-0">
                <VideoPlayerRiv key={stop.riv} videoName={stop.riv} width="w-16" height="h-16" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="body-3-medium font-primary flex items-center gap-1.5">
                    <IconSparkles size={14} className="font-blue shrink-0" />
                    {t(stop.titleKey)}
                  </span>
                  <span className="flex items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => setUserCollapsed(true)}
                      aria-label={t("collapse")}
                      className="shrink-0 p-0.5 rounded-md hover:surface-quaternary transition-colors cursor-pointer"
                    >
                      <IconMinus size={14} className="font-tertiary" />
                    </button>
                    <button
                      type="button"
                      onClick={() => endJourney("SKIPPED")}
                      aria-label={t("dismiss")}
                      className="shrink-0 p-0.5 rounded-md hover:surface-quaternary transition-colors cursor-pointer"
                    >
                      <IconX size={14} className="font-tertiary" />
                    </button>
                  </span>
                </div>
                <p className="body-4-regular font-secondary mt-1">{bodyText}</p>

                <div className="flex items-center justify-between gap-2 mt-2.5">
                  <span className="body-4-regular font-tertiary">
                    {stop.progress
                      ? t("stepOf", { n: stop.progress.index, total: stop.progress.total })
                      : ""}
                  </span>
                  {stop.id === "allSet" ? (
                    <button
                      type="button"
                      onClick={() => endJourney("COMPLETED")}
                      className="body-3-medium font-blue hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      {t("finish")}
                    </button>
                  ) : stop.next && eventId ? (
                    <button
                      type="button"
                      onClick={() => navigate(stop.next!(eventId))}
                      className="inline-flex items-center gap-1 body-3-medium font-blue hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      {t("next")}
                      <IconArrowRight size={14} />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
